import { ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { HistoricalTrends } from '../../interfaces/historical-trends';
import { HistoricalTrendsService } from '../../services/historical-trends.service';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { MilestoneService } from '../../../shared/services/milestone.service';
import { DisplayOptions, IHistoricalYears } from '../../../shared/interfaces/display-options';
import { Router } from '@angular/router';
import { Milestone } from '../../../shared/interfaces/milestone';
import { ILargeCardInteriorOptions } from '../../../shared/interfaces/card-data';
import { NoDataActionType } from '../../../shared/interfaces/no-data';
import * as Highcharts from 'highcharts';
import { UserAccessService } from '../../../shared/services/user-access.service';
import { DisplayOptionsService } from '../../../shared/services/display-options.service';
import { CustomDashboardsService } from 'app/modules/my-limegear/services/my-limegear.service';
import { ComponentsOutletDirectiveService } from 'app/modules/my-limegear/services/component-outlet-directive.service';
import { CustomDashboardsOutletDirective } from 'app/modules/my-limegear/my-limegear-outlet.directive';

interface IChartInfo {
    drillDUrlSegment?: string;
    databaseName: string;
    name: string;
    unitsScaled: boolean;
    volumeScaled: boolean;
    milestoneInfo?: Milestone;
    largeCardInteriorOptions?: ILargeCardInteriorOptions;
}

@Component({
    selector: 'app-historical-trends',
    templateUrl: './historical-trends.component.html',
    styleUrls: ['./historical-trends.component.scss'],
})
export class HistoricalTrendsComponent implements OnInit {
    pageTitle: string = 'Historical Trends At a Glance';
    historicalTrendsCharts: IChartInfo[] = [
        {
            drillDUrlSegment: 'creditReports',
            databaseName: 'Credit Inquiry',
            name: 'Credit Reports',
            unitsScaled: true,
            volumeScaled: false,
        },
        {
            drillDUrlSegment: 'applications',
            databaseName: 'Application',
            name: 'Applications',
            unitsScaled: true,
            volumeScaled: false,
        },
        {
            drillDUrlSegment: 'locks',
            databaseName: 'Locked',
            name: 'Locks',
            unitsScaled: false,
            volumeScaled: true,
        },
        {
            drillDUrlSegment: 'funded',
            databaseName: 'Funded',
            name: 'Funded',
            unitsScaled: false,
            volumeScaled: true,
        },
    ];

    yearOptions: IHistoricalYears[] = [];
    colorOptions: object = {};
    milestones: Milestone[];
    displayOptions: DisplayOptions[];
    hFilterJSON: string = '';
    yMaxUnits: number;
    yMaxVolume: number;
    action: NoDataActionType;
    drillDownBaseUrl = '/reporting/historical-trends';

    // Custom Dashboard
    dashboard = 'historical-trends';
    largeCardIdArray: Array<string>;

    constructor(
        private _displayOptionsService: DisplayOptionsService,
        private _router: Router,
        private reusableFunctions: ReusableFunctions,
        private _userAccessService: UserAccessService,
        private customDashboardsService: CustomDashboardsService,
        private componentsOutletDirectiveService: ComponentsOutletDirectiveService,
        private cd: ChangeDetectorRef
    ) {}

    @ViewChildren(CustomDashboardsOutletDirective)
    dashboardOutlet: QueryList<CustomDashboardsOutletDirective>;

    ngOnInit() {
        this.yearOptions = JSON.parse(localStorage.getItem('historical-year-options'));
        this.colorOptions = this.reusableFunctions.getColorOptions('historical');
        this.customDashboardsService.destroyCharts();
        this.customDashboardsService.detectChange(
            '',
            [],
            false,
            false,
            NoDataActionType.pageStart,
            this.dashboard
        );

        this.largeCardIdArray = [
            'historical-trends-credit-reports',
            'historical-trends-applications',
            'historical-trends-locked',
            'historical-trends-funded',
        ];
        this.updateComponent();
    }

    setFilterOptions() {
        this.initializeCharts(false);
        this.hFilterJSON = this.reusableFunctions.getHierarchyFilters();
        this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(this._router.url);
        this.action = NoDataActionType.pageStart;
    }

    getUniqueId(urlSegment: string) {
        let urlitems = (this.drillDownBaseUrl + urlSegment).split('/');
        let id1 = urlitems[urlitems.length - 2] ? urlitems[urlitems.length - 2] : 'id-first';
        let id2 = urlitems[urlitems.length - 1] ? urlitems[urlitems.length - 1] : 'id-second';
        this.drillDownBaseUrl + urlSegment;
        return (id1 + id2).toLowerCase();
    }

    initializeCharts(chartReset = false) {
        this.historicalTrendsCharts.forEach((hInfo) => {
            let emptyChart = null;
            if (chartReset) {
                emptyChart = new Highcharts.Chart(this.getUniqueId(hInfo.drillDUrlSegment), {});
            }

            hInfo.largeCardInteriorOptions = {
                action: this.action,
                chart: emptyChart,
                componentTitle: hInfo.name,
                searchTerm: '',
                hasData: false,
                isDataReady: false,
                showTitle: true,
                accessToUrl: this.drillDownBaseUrl + '/' + hInfo.drillDUrlSegment,
                uniqueId: this.getUniqueId(hInfo.drillDUrlSegment),
            };

            hInfo.largeCardInteriorOptions = Object.assign({}, hInfo.largeCardInteriorOptions);
        });
    }

    resetYearLocalStorage() {
        //TODO: this method can be removed once proper tackble method introduced in toggleYear()
        let yearOptions: IHistoricalYears[] = JSON.parse(
            localStorage.getItem('historical-year-options')
        );

        yearOptions.forEach((item) => {
            item.yearInfo.visible = true;
        });

        localStorage.setItem('historical-year-options', JSON.stringify(yearOptions));
    }

    setLineChartVisibility(year: number, visibilityFlag: boolean) {
        this.historicalTrendsCharts.forEach((chartSeries) => {
            let allSeries = chartSeries.largeCardInteriorOptions.chart.series;
            if (allSeries) {
                let series = allSeries.find((item) => item.name === year.toString());
                if (series) {
                    series.setVisible(visibilityFlag);
                }
            }
        });
    }

    toggleYear(year, visibilityFlag: boolean) {
        visibilityFlag = !visibilityFlag;
        this.yearOptions.find((item) => item.year === year).yearInfo.visible = visibilityFlag;
        localStorage.setItem('historical-year-options', JSON.stringify(this.yearOptions));
        this._displayOptionsService.syncYearTogalWithDrilldownDisplayoptions();

        // JBC removed this line as it was throwing errors after moving to custom dashboard
        //this.setLineChartVisibility(year, visibilityFlag);

        // JBC added this line to update the charts after toggling the year
        this.updateComponent();
    }

    showAllDrilldownData(historicalTrendsChart: IChartInfo) {
        this._router.navigate(
            [this.drillDownBaseUrl, historicalTrendsChart.drillDUrlSegment + '-viewall'],
            {
                queryParams: {
                    milestonekey: historicalTrendsChart.milestoneInfo.milestoneKey,
                },
            }
        );
    }

    checkAccess(url: string) {
        return this._userAccessService.hasAccessToPage(url);
    }

    applyNewFilters(event: string) {
        this.customDashboardsService.destroyCharts();
        this.hFilterJSON = event;
        this.updateComponent();
        this.customDashboardsService.detectChange(
            this.hFilterJSON,
            this.displayOptions,
            true,
            false,
            NoDataActionType.filter,
            this.dashboard
        );
    }

    updateComponent() {
        this.componentsOutletDirectiveService.detectViewChanges(this.cd);
        this.componentsOutletDirectiveService.loadContents(this.dashboardOutlet);
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.customDashboardsService.destroyCharts();
        this.displayOptions = event;
        this.updateComponent();
        this.customDashboardsService.detectChange(
            this.hFilterJSON,
            this.displayOptions,
            false,
            true,
            NoDataActionType.filter,
            this.dashboard
        );
    }

    ngOnDestroy() {
        this.customDashboardsService.unsubscribeFilters();
        this.customDashboardsService.destroyCharts();
    }
}
